// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

import * as React from 'react';

import clsx from 'clsx';

import { ResolvedPatternGroup } from './patternGroups';

import styles from './PatternGroupOverlay.module.css';

export interface PatternGroupOverlayProps {
  /**
   * Pre-resolved pattern groups (one entry per group whose
   * variables resolved to at least one visible view element).
   * Order is honored: later entries paint on top, so callers can
   * encode an explicit z-stacking by ordering this array.
   */
  groups: ReadonlyArray<ResolvedPatternGroup>;
}

/**
 * Padding from the boundary stroke to the label baseline.  Matches
 * the visual gap between the boundary corner and the label glyph.
 */
const LABEL_PADDING = 6;

/**
 * D3 acceptance criterion 31 -- pattern-group overlay.
 *
 * Renders one labeled boundary per resolved pattern group.  The
 * boundary is a rounded, dashed-stroke rectangle with a very faint
 * tinted fill so the variables underneath remain fully legible.
 * The label sits at the top-left inside corner of the boundary so
 * it reads as a section header.
 *
 * Mirrors the C28 / C29 / C30 contract:
 *
 *   1. No engine imports.  The resolver handles the join with
 *      `ViewElement`; the renderer sees only the resolved geometry.
 *      Enforced by the source audit in
 *      [tests/pattern-group-readonly.test.ts](../tests/pattern-group-readonly.test.ts).
 *
 *   2. No callbacks in props.  Pointer events pass through so the
 *      boundary doesn't intercept clicks intended for the variables
 *      it surrounds.
 */
export const PatternGroupOverlay = React.memo(function PatternGroupOverlay(
  props: PatternGroupOverlayProps,
): React.ReactElement | null {
  const { groups } = props;

  if (groups.length === 0) {
    return null;
  }

  return (
    <g className={clsx(styles.overlay, 'simlin-pattern-group-overlay')}>
      {groups.map((group) => (
        // When `group.kind` is present we emit an extra
        // `simlin-pattern-group-<kind>` class so downstream
        // stylesheets can attach archetype-specific chrome
        // (e.g. a yellow dashed boundary for `limits-to-growth`)
        // without code changes.  Added in v1.0 of the C34 schema
        // review (Q3 resolution).  React's className attribute
        // value is escaped at render so a malformed kind cannot
        // inject HTML; an exotic kind just produces a CSS class
        // that no selector happens to match.
        <g
          key={group.uid}
          className={clsx(
            'simlin-pattern-group',
            group.kind !== undefined ? `simlin-pattern-group-${group.kind}` : undefined,
          )}
        >
          <rect
            className={clsx(styles.boundary, 'simlin-pattern-group-boundary')}
            x={group.x}
            y={group.y}
            width={group.width}
            height={group.height}
            rx={8}
            ry={8}
            stroke={group.color}
          />
          <text
            className={clsx(styles.label, 'simlin-pattern-group-label')}
            x={group.x + LABEL_PADDING}
            y={group.y + LABEL_PADDING}
            fill={group.color}
          >
            {group.label}
          </text>
        </g>
      ))}
    </g>
  );
});
